/**
 * Малые графики экрана «Вода»: покрытие по поливам цикла (в KPI), каналы
 * выбранного полива и двусторонний разрез хозяйств цикла.
 *
 * Только оттенки воды и янтарь недоподачи (ADR-0009): подано — `--aqua`,
 * перерасход — `--aqua-deep`, недоподача — `--st-warn`. Зелёного в данных
 * нет: подача — объём, а не оценка.
 */
import { InfoPopover } from '../../components/InfoPopover';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { Icon } from '../ui/Icon';
import { EmptyState } from '../ui/EmptyState';
import { countOf, m3, m3Compact, num, pct, WORDS } from '../lib/format';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { tr } from '../../i18n/runtime';
import type { ChannelRow, FarmWaterRow, Shortfall } from './waterSummary';
import { divergingFarms } from './waterSummary';

const CHANNEL_HEAD = 8;

const m = defineMessages({
  noData: {
    id: 'water.charts.noData',
    defaultMessage: 'нет данных',
    description: 'Значение вместо числа, когда подачи или запроса нет: не ноль, а отсутствие данных (подпись столбика покрытия, значение канала)',
  },
  stripLabel: {
    id: 'water.charts.coverageStrip.label',
    defaultMessage: 'Покрытие по поливам — {text}',
    description: 'aria-label мини-графика столбиков «подано от запрошенного» по поливам агроцикла; {text} — перечень «полив: процент» через точку с запятой',
  },
  stripPoint: {
    id: 'water.charts.coverageStrip.point',
    defaultMessage: '{label}: {value}',
    description: 'Один пункт подсказки мини-графика покрытия: {label} — название полива, {value} — процент покрытия или «нет данных»',
  },
  noChannelsTitle: {
    id: 'water.charts.channels.emptyTitle',
    defaultMessage: 'Каналов нет',
    description: 'Заголовок пустого состояния списка каналов выбранного полива (менеджер, хоким)',
  },
  noChannelsReason: {
    id: 'water.charts.channels.emptyReason',
    defaultMessage: 'подач с распределением по контурам в этом поливе нет',
    description: 'Пояснение пустого состояния списка каналов: в поливе нет подач воды, распределённых по контурам; строчными, после заголовка',
  },
  channelHint: {
    id: 'water.charts.channels.hint',
    defaultMessage: 'Нажмите канал — на карте и в таблице останутся только его контуры.',
    description: 'Подсказка над списком каналов выбранного полива: клик по каналу фильтрует карту и таблицу',
  },
  channelInfoLabel: {
    id: 'water.charts.channels.infoLabel',
    defaultMessage: 'Как читать полосы каналов',
    description: 'aria-label и заголовок кнопки-«i» рядом с подсказкой над списком каналов',
  },
  channelInfoBody: {
    id: 'water.charts.channels.infoBody',
    defaultMessage:
      'Полоса — подано от запрошенного через канал, черта — 100 %. Число справа — доля и объём поданного. Под каналом — хозяйства, которым он отдал воду в этом поливе: сначала с наибольшей недоподачей в м³.',
    description: 'Текст всплывающего пояснения «Как читать полосы каналов»; 100 % — граница полного покрытия на полосе',
  },
  channelInfoBodyEvents: {
    id: 'water.charts.channels.infoBodyEvents',
    defaultMessage:
      'Полоса — объём, поданный через канал, относительно канала с наибольшим объёмом в этом поливе. Число справа — объём по событиям файла ирригации. Под каналом — хозяйства, которым он отдал воду в этом поливе.',
    description: 'Текст всплывающего пояснения «Как читать полосы каналов», когда вода считается по событиям файла (заявок нет): полоса не доля от запрошенного',
  },
  channelTitleEvents: {
    id: 'water.charts.channels.rowTitleEvents',
    defaultMessage: 'канал {label}: подано {delivered}; хозяйства: {farms}',
    description: 'Всплывающая подсказка строки канала по событиям файла. {label} — название канала, {delivered} — объём воды с единицей (м³), {farms} — хозяйства через запятую или «не названы»',
  },
  channelTitle: {
    id: 'water.charts.channels.rowTitle',
    defaultMessage: 'канал {label}: подано {delivered} из {requested}; хозяйства: {farms}',
    description: 'Всплывающая подсказка строки канала. {label} — название канала, {delivered}/{requested} — объёмы воды с единицей (м³), {farms} — хозяйства через запятую или «не названы»',
  },
  farmsNotNamed: {
    id: 'water.charts.channels.farmsNotNamed',
    defaultMessage: 'не названы',
    description: 'Вместо перечня хозяйств в подсказке канала, когда источник хозяйств не назвал; строчными, после «хозяйства:»',
  },
  channelName: {
    id: 'water.charts.channels.name',
    defaultMessage: 'канал {label}',
    description: 'Название канала в строке списка; {label} — имя или номер канала из данных',
  },
  channelFarmsArrow: {
    id: 'water.charts.channels.farmsArrow',
    defaultMessage: '→ {farms}',
    description: 'Под названием канала: стрелка и перечень хозяйств, которым канал отдал воду; {farms} — готовая фраза «N хозяйств: А −1 000 м³ · Б»',
  },
  showAll: {
    id: 'water.charts.channels.showAll',
    defaultMessage: 'Показать все {count}',
    description: 'Кнопка под списком каналов: показать все каналы; {count} — их число',
  },
  collapse: {
    id: 'water.charts.channels.collapse',
    defaultMessage: 'Свернуть',
    description: 'Кнопка под списком каналов: свернуть список до первых восьми',
  },
  channelFarmsNone: {
    id: 'water.charts.channels.farmsNone',
    defaultMessage: 'хозяйства не названы',
    description: 'Под названием канала, когда хозяйства, получившие воду, не названы источником',
  },
  channelFarms: {
    id: 'water.charts.channels.farms',
    defaultMessage: '{farms}: {head}{more, select, yes { …} other {}}',
    description: 'Кому канал отдал воду: {farms} — число хозяйств словом («12 хозяйств»), {head} — первые три хозяйства (с недоподачей в м³ через «−»), {more} — yes, если хозяйств больше трёх',
  },
  chipChannel: {
    id: 'water.charts.channels.chip',
    defaultMessage: 'Канал: {label}',
    description: 'Чип активного фильтра канала над таблицей (нажатие снимает фильтр); {label} — название канала',
  },
  noGapTitle: {
    id: 'water.charts.farms.noGapTitle',
    defaultMessage: 'Разрыва нет',
    description: 'Заголовок пустого состояния диаграммы разрыва по хозяйствам агроцикла: ни у одного хозяйства подано не меньше и не больше запрошенного',
  },
  noGapReason: {
    id: 'water.charts.farms.noGapReason',
    defaultMessage: 'у {farms} по поливам агроцикла подано столько, сколько запрошено, или объём не измерен',
    description: 'Пояснение пустого состояния диаграммы разрыва; строчными, после заголовка. {farms} — число хозяйств словом («12 хозяйств»)',
  },
  rowTitle: {
    id: 'water.charts.farms.rowTitle',
    defaultMessage: 'запрошено {requested}, подано {delivered}',
    description: 'Подсказка к значению в строке диаграммы разрыва; {requested}/{delivered} — объёмы воды с единицей (м³)',
  },
  legendUnder: {
    id: 'water.charts.farms.legendUnder',
    defaultMessage: 'недоподача · {farms}',
    description: 'Легенда диаграммы разрыва: цвет недоподачи (подано меньше заявки); {farms} — число хозяйств словом («5 хозяйств»)',
  },
  legendOver: {
    id: 'water.charts.farms.legendOver',
    defaultMessage: 'перерасход · {farms}',
    description: 'Легенда диаграммы разрыва: цвет перерасхода (подано больше заявки); {farms} — число хозяйств словом',
  },
  divergingNote: {
    id: 'water.charts.farms.note',
    defaultMessage: 'Крупнейшие {under} по недоподаче и {over} по перерасходу по поливам агроцикла.',
    description: 'Подпись под диаграммой разрыва по хозяйствам; {under}/{over} — числа (сколько хозяйств показано с каждой стороны)',
  },
  gapInfoLabel: {
    id: 'water.charts.farms.infoLabel',
    defaultMessage: 'Что такое разрыв',
    description: 'aria-label и заголовок кнопки-«i» рядом с подписью диаграммы разрыва',
  },
  gapInfoBody: {
    id: 'water.charts.farms.infoBody',
    defaultMessage:
      'Разрыв — запрошено минус подано по распределениям хозяйства. Недоподача — подано меньше заявки, перерасход — больше. Объём не измерен — пробел учёта, а не ноль.',
    description: 'Текст всплывающего пояснения «Что такое разрыв» на экране «Вода»',
  },
});

/* ------------------------------------------------ покрытие по поливам */

export interface CoveragePoint {
  key: string;
  label: string;
  coveragePct: number | null;
  current: boolean;
}

/**
 * Столбики «подано от запрошенного» по поливам цикла — вместо отдельного
 * недельного графика. Полив без счёта — пустое место, не нулевой столбик.
 */
export function CoverageStrip({ points }: { points: CoveragePoint[] }) {
  const t = useT();
  if (points.length < 2) return null;
  const text = points
    .map((p) => t(m.stripPoint, { label: p.label, value: p.coveragePct === null ? t(m.noData) : pct(p.coveragePct, 1) }))
    .join('; ');
  return (
    <span className="wv-strip" role="img" aria-label={t(m.stripLabel, { text })} title={text}>
      {points.map((p) => (
        <span key={p.key} className="wv-strip__slot">
          {p.coveragePct === null ? null : (
            <span
              className={`wv-strip__bar ${p.current ? 'is-current' : ''}`}
              style={{ height: `${Math.max(8, Math.min(100, p.coveragePct))}%` }}
            />
          )}
        </span>
      ))}
    </span>
  );
}

/* --------------------------------------------------------------- каналы */

export function ChannelList({
  rows,
  selected,
  onSelect,
  events = false,
}: {
  rows: ChannelRow[];
  selected: string | null;
  onSelect: (key: string | null) => void;
  /** Вода по событиям файла: полоса — относительно крупнейшего канала, без «запрошено» и покрытия. */
  events?: boolean;
}) {
  const t = useT();
  const [all, setAll] = useState(false);
  if (rows.length === 0) {
    return <EmptyState compact title={t(m.noChannelsTitle)} reason={t(m.noChannelsReason)} />;
  }
  const maxDelivered = Math.max(0, ...rows.map((c) => c.deliveredM3 ?? 0));
  return (
    <div className="wv-bars">
      <p className="t-caption wv-hint">
        {t(m.channelHint)}
        <InfoPopover icon="info" label={t(m.channelInfoLabel)}>
          {events ? t(m.channelInfoBodyEvents) : t(m.channelInfoBody)}
        </InfoPopover>
      </p>
      <ul className="wv-bars__list">
        {(all ? rows : rows.filter((c, i) => i < CHANNEL_HEAD || c.key === selected)).map((c) => {
          const ratio = events ? (maxDelivered > 0 ? ((c.deliveredM3 ?? 0) / maxDelivered) * 100 : 0) : (c.coveragePct ?? 0);
          const on = c.key === selected;
          return (
            <li key={c.key || 'none'}>
              <button
                type="button"
                className={`wv-chan focus-ring ${on ? 'is-on' : ''}`}
                aria-pressed={on}
                onClick={() => onSelect(on ? null : c.key)}
                title={
                  events
                    ? t(m.channelTitleEvents, { label: c.label, delivered: m3(c.deliveredM3), farms: c.farms?.map((f) => f.name).join(', ') || t(m.farmsNotNamed) })
                    : t(m.channelTitle, { label: c.label, delivered: m3(c.deliveredM3), requested: m3(c.requestedM3), farms: c.farms?.map((f) => f.name).join(', ') || t(m.farmsNotNamed) })
                }
              >
                <span className="wv-chan__name">
                  {t(m.channelName, { label: c.label })}
                  {c.farms ? <span className="wv-chan__farms">{t(m.channelFarmsArrow, { farms: channelFarmsText(c.farms) })}</span> : null}
                </span>
                <span className="wv-chan__track" aria-hidden="true">
                  <span className="wv-chan__fill" style={{ width: `${events ? Math.min(100, ratio) : Math.min(100, ratio / 1.2)}%` }} />
                  {events ? null : <span className="wv-chan__mark" style={{ left: `${100 / 1.2}%` }} />}
                </span>
                <span className="wv-chan__val">
                  {events ? (
                    m3Compact(c.deliveredM3)
                  ) : (
                    <>
                      {c.coveragePct === null ? t(m.noData) : pct(c.coveragePct, 1)}
                      <span className="wv-chan__sub">{m3Compact(c.deliveredM3)}</span>
                    </>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {rows.length > CHANNEL_HEAD ? (
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => setAll((v) => !v)}>
          {all ? t(m.collapse) : t(m.showAll, { count: num(rows.length) })}
        </button>
      ) : null}
    </div>
  );
}

/** «→ 12 хозяйств: А −4 561 м³ · Б · В» — кому канал отдал воду (В3, №651). */
export function channelFarmsText(farms: NonNullable<ChannelRow['farms']>): string {
  if (!farms.length) return tr(m.channelFarmsNone);
  const head = farms.slice(0, 3).map((f) => (f.shortM3 ? `${f.name} −${m3Compact(f.shortM3)}` : f.name)).join(' · ');
  const n = farms.length;
  return tr(m.channelFarms, { farms: countOf(n, WORDS.farm), head, more: n > 3 ? 'yes' : 'no' });
}

/** Активный фильтр канала — чип со снятием. */
export function ChannelChip({ label, onClear }: { label: string; onClear: () => void }) {
  const t = useT();
  return (
    <button type="button" className="wv-chip focus-ring" onClick={onClear}>
      {t(m.chipChannel, { label })}
      <Icon icon={X} size={16} />
    </button>
  );
}

/* ---------------------------------------------------------- хозяйства */

/**
 * Одна двусторонняя диаграмма: недоподача влево (янтарь), перерасход вправо
 * (глубокая вода). Шкала общая — длины сравнимы между сторонами.
 */
export function FarmDiverging({ rows, shortfall }: { rows: FarmWaterRow[]; shortfall: Shortfall | null }) {
  const t = useT();
  const d = divergingFarms(rows, shortfall);
  if (d.under.length === 0 && d.over.length === 0) {
    return (
      <EmptyState
        compact
        title={t(m.noGapTitle)}
        reason={t(m.noGapReason, { farms: countOf(rows.length, WORDS.farm) })}
      />
    );
  }
  const line = (r: FarmWaterRow, side: 'under' | 'over') => {
    const value = side === 'under' ? r.shortM3! : r.overM3!;
    const width = `${(value / d.max) * 100}%`;
    return (
      <li key={`${side}-${r.actorId}`} className="wv-div__row">
        <Link className="wv-div__name" to={`/farms/${r.actorId}`} title={r.name}>
          {r.name}
        </Link>
        <span className="wv-div__left">{side === 'under' ? <span className="wv-div__bar wv-div__bar--under" style={{ width }} /> : null}</span>
        <span className="wv-div__right">{side === 'over' ? <span className="wv-div__bar wv-div__bar--over" style={{ width }} /> : null}</span>
        <span className="wv-div__val" title={t(m.rowTitle, { requested: m3(r.requestedM3), delivered: m3(r.deliveredM3) })}>
          {side === 'under' ? '−' : '+'}
          {m3Compact(value)}
        </span>
      </li>
    );
  };
  return (
    <div className="wv-div">
      <div className="wv-div__legend t-caption">
        <span>
          <i className="wv-div__swatch wv-div__swatch--under" aria-hidden="true" /> {t(m.legendUnder, { farms: countOf(d.underCount, WORDS.farm) })}
        </span>
        <span>
          <i className="wv-div__swatch wv-div__swatch--over" aria-hidden="true" /> {t(m.legendOver, { farms: countOf(d.overCount, WORDS.farm) })}
        </span>
      </div>
      <ul className="wv-div__list">
        {d.under.map((r) => line(r, 'under'))}
        {d.over.map((r) => line(r, 'over'))}
      </ul>
      <p className="t-caption wv-hint">
        {t(m.divergingNote, { under: num(d.under.length), over: num(d.over.length) })}
        <InfoPopover icon="info" label={t(m.gapInfoLabel)}>
          {t(m.gapInfoBody)}
        </InfoPopover>
      </p>
    </div>
  );
}
